import { cn } from '@/utils/cn';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { handleDomBubbleEvent } from '@/utils/dom';
import Modal from '@/components/_ui/modal/modal';
import Button from '@/components/_ui/button/button';
import { useCallback } from 'react';

export interface OTPFormData {
  otp1: string;
  otp2: string;
  otp3: string;
  otp4: string;
}

export interface IOTPModalProps {
  handleSubmit: (data: OTPFormData) => void;
  handleResend: () => void;
  loading?: boolean;
  onClose?: () => void;
}

export type TOTPModalType = 'OTPModal';

const otpSchema = z.object({
  otp1: z.string().min(1).max(1),
  otp2: z.string().min(1).max(1),
  otp3: z.string().min(1).max(1),
  otp4: z.string().min(1).max(1),
});

type TOtpSchema = z.infer<typeof otpSchema>;

const OTPModal = ({ handleSubmit: onSubmit, handleResend, loading = false, onClose }: IOTPModalProps) => {
  const form = useForm<TOtpSchema>({
    defaultValues: {
      otp1: '',
      otp2: '',
      otp3: '',
      otp4: '',
    },
    resolver: zodResolver(otpSchema),
  });

  const handleChange = useCallback(
    (value: string, nextField?: keyof OTPFormData) => {
      if (value.length === 1 && nextField) {
        form.setFocus(nextField);
      }
    },
    [form]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, prevField?: keyof OTPFormData) => {
      if (e.key === 'Backspace' && e.currentTarget.value === '' && prevField) {
        form.setFocus(prevField);
      }
    },
    [form]
  );

  const hasError = useCallback(() => Object.keys(form.formState.errors).length > 0, [form.formState.errors]);

  useEffect(() => {
    form.reset({
      otp1: '',
      otp2: '',
      otp3: '',
      otp4: '',
    });
  }, []);

  const handleSubmit = (data: OTPFormData) => {
    if (hasError()) return;
    onSubmit(data);
  };

  return (
    <Modal
      isOpen={true}
      isCloseOutside={false}
      isButtonClose={false}
      onClose={onClose || (() => {})}
      className="w-[400px] max-w-[400px]"
    >
      <div className="pb-6 flex flex-col gap-2">
        <h3 className="font-bold text-lg text-primary text-center">OTP Verification</h3>
        <p className="text-center text-xs text-others-dark-gray">Please type verification code sent to your email</p>
      </div>
      <form
        onSubmit={(e) => {
          handleDomBubbleEvent(e);
          form.handleSubmit(handleSubmit)(e);
        }}
      >
        <div className="flex items-center justify-center gap-3 text-[#25151E] mt-[8px]">
          {(['otp1', 'otp2', 'otp3', 'otp4'] as Array<keyof OTPFormData>).map((name, index, arr) => (
            <Controller
              key={name}
              name={name}
              control={form.control}
              render={({ field: { onChange, value, ref } }) => (
                <input
                  ref={ref}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  pattern="\d*"
                  className={cn(
                    'border w-12 h-12 rounded-md text-center text-[18px] font-bold border-[#D9D9D9] focus:border-[#0C8DBF] focus:outline-[#0C8DBF]',
                    form.formState.errors[name] && 'border-[#CC0202] focus:border-[#CC0202] focus:ring-[#CC0202]'
                  )}
                  value={value}
                  onChange={(e) => {
                    const value = Number(e.target.value);

                    if (isNaN(value)) {
                      // ignore not number value
                      return;
                    }
                    onChange(e.target.value);
                    handleChange(e.target.value, arr[index + 1]);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, arr[index - 1])}
                />
              )}
            />
          ))}
        </div>
        <div className={cn('label px-6 justify-center', hasError() ? 'visible' : 'invisible')}>
          <span className="label-text-alt text-error">Please complete the form.</span>
        </div>
      </form>
      <div className="flex justify-center">
        <div className="px-6 w-full">
          <Button
            variant="primary"
            disabled={loading || form.formState.isSubmitting}
            isLoading={loading || form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
            className="w-full"
          >
            Submit
          </Button>
          <Button
            variant="ghost"
            disabled={loading || form.formState.isSubmitting}
            onClick={() => handleResend()}
            className="w-full mt-3"
          >
            Resend code
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default OTPModal;
